import { supabase } from '../lib/supabase';

export interface DeleteAccountRequest {
  email: string;
  password: string;
}

export interface DeleteAccountResponse {
  success: boolean;
  message: string;
  error?: string;
  isOwner?: boolean;
}

export const deleteUserAccount = async (
  request: DeleteAccountRequest
): Promise<DeleteAccountResponse> => {
  try {


    // First, authenticate the user to verify credentials
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: request.email,
      password: request.password,
    });



    if (authError) {
      console.error('Authentication failed:', authError);
      return {
        success: false,
        message: 'Invalid email or password. Please check your credentials.',
        error: authError.message,
      };
    }

    const userId = authData.user?.id;
    if (!userId) {
      return {
        success: false,
        message: 'User not found',
        error: 'No user ID found in authentication response',
      };
    }

    // Check if user is an owner or admin in any company
    const { data: relationships, error: relationshipError } = await supabase
      .rpc('get_user_relationships_with_roles', { p_user_id: userId });

    if (relationshipError) {
      console.error('Error fetching user relationships:', relationshipError);
      return {
        success: false,
        message: 'Failed to verify user permissions',
        error: relationshipError.message,
      };
    }

    // Check if user is an owner in any company (admins can delete directly)
    // Relationship type from Supabase query - structure varies
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isOwner = relationships?.some((rel: any) => 
      rel.role === 'owner'
    );

    if (isOwner) {
      // For owners, send support email but don't delete account yet
      // Support will follow up to discuss retention and handle deletion
      try {
        // Get the first company where user is owner for the support request
        // Relationship type from Supabase query - structure varies
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ownerCompany = relationships?.find((rel: any) => 
          rel.role === 'owner'
        );

        if (!ownerCompany) {
          throw new Error('No owner company found');
        }

        // Send support request via Edge Function
        try {
          await supabase.functions.invoke('send-support-email', {
            body: {
              user_id: userId,
              company_id: ownerCompany.company_id,
              request_type: 'account_deletion',
              subject: 'Account Deletion Request - Gym Owner Review Required',
              description: 'This gym owner has requested account deletion. Support should follow up within 1-2 business days to discuss account retention options and handle the deletion process, including customer data and contacts management.',
              priority: 'high'
            }
          });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (emailError: any) {
          // Log but continue - will show error to user
          console.error('Failed to send support email:', emailError);
          return {
            success: false,
            message: 'Failed to submit support request. Please contact support@rollops.pro directly.',
            error: emailError.message,
          };
        }

        // Sign out the current session (don't delete account yet)
        await supabase.auth.signOut();

        return {
          success: true,
          message: 'Your account deletion request has been sent to our support team. We will review your request and contact you within 1-2 business days to discuss account retention options and handle the deletion process.',
          isOwner: true,
        };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.error('Error during owner account deletion request:', error);
        return {
          success: false,
          message: 'Failed to submit support request. Please contact support@rollops.pro directly.',
          error: error.message,
        };
      }
    }

    // For admins and regular users, proceed with account deletion using the edge function
    try {
      const { error: deleteError } = await supabase.functions.invoke('delete-user-account', {
        body: {}
      });

      if (deleteError) {
        console.error('Account deletion failed:', deleteError);
        return {
          success: false,
          message: 'Failed to delete account. Please try again later.',
          error: deleteError.message,
        };
      }

      // Sign out the current session
      await supabase.auth.signOut();

      return {
        success: true,
        message: 'Your account and personal data have been successfully deleted.',
        isOwner: false,
      };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (deleteError: any) {
      console.error('Error during account deletion:', deleteError);
      return {
        success: false,
        message: 'Failed to delete account. Please try again later.',
        error: deleteError.message,
      };
    }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {

    return {
      success: false,
      message: 'An unexpected error occurred: ' + error.message,
      error: error.message || 'Unknown error',
    };
  }
};
